import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry sent — we'll get back to you from the pit lane.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-white/20 focus:border-primary px-0 py-4 text-sm outline-none transition-colors duration-300 placeholder:text-foreground/30 rounded-none";

  return (
    <section id="contact" className="py-28 md:py-44 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs font-semibold tracking-[0.4em] uppercase text-primary mb-6">Contact</p>
          <h2 className="font-display uppercase leading-[0.9] text-5xl md:text-7xl" data-testid="contact-heading">
            Talk to the<br /><span className="text-stroke">Pit Wall</span>
          </h2>
          <p className="font-editorial italic text-lg md:text-xl text-foreground/60 mt-8 max-w-md">
            Rights enquiries, bulk editions, press, or a story we haven't told yet —
            every message lands directly in our inbox.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-8"
          data-testid="contact-form"
        >
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name"
            className={field}
            data-testid="contact-name-input"
          />
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Your email"
            className={field}
            data-testid="contact-email-input"
          />
          <textarea
            required
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="What's on your mind?"
            className={`${field} resize-none`}
            data-testid="contact-message-input"
          />
          <button
            type="submit"
            disabled={sending}
            className="group flex items-center gap-4 bg-primary text-white font-display uppercase tracking-wider text-sm px-10 py-4 hover:bg-[#a00d26] active:scale-95 disabled:opacity-50 transition-[background-color,transform,opacity] duration-200"
            data-testid="contact-submit-button"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : null}
            Send Enquiry
          </button>
        </motion.form>
      </div>
    </section>
  );
};
